import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SizeTypeItem from '../SizeTypeItemComponent';
import ContainerInfoComponent from '../ContainerInfoComponent';
import Sidebar from '../../../ReusableComponents/Sidebar';

export default function ContainerSelectionBody({
  sizeTypes,
  selectedSize,
  setSelectedSize,
  selectedLevel,
  setSelectedLevel,
  roomList,
  handleNext,
}) {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedIsIndoorFilter, setSelectedIsIndoorFilter] = useState(false);
  const [sortedSizeTypes, setSortedSizeTypes] = useState([]);
  const [siteHasIndoor, setSiteHasIndoor] = useState(false);

  // Sort by availability so the ones where we have free containers will apear in the list first.
  const computeVolume = type => {
    const sizeTypeLength = Number(type.length || type.l || 0);
    const sizeTypeWidth = Number(type.width || type.w || 0);
    const sizeTypeHeight = Number(type.height || type.h || 0);
    return sizeTypeLength * sizeTypeWidth * sizeTypeHeight;
  };

  // Sort by size so the container that are smallet will apear first
  useEffect(() => {
    if (Array.isArray(sizeTypes) && sizeTypes.length > 0) {
      setSiteHasIndoor(sizeTypes.some(type => type.isIndoor));

      const projected = sizeTypes
        .map(type => {
          const orientationStats = selectedIsIndoorFilter
            ? type.indoor
            : type.outdoor;
          if (!orientationStats) return null;

          const hasAvailability = orientationStats.availableAmount > 0;
          const isSoldOutEverywhere =
            (type.indoor?.availableAmount || 0) === 0 &&
            (type.outdoor?.availableAmount || 0) === 0;
          // When sold out in both orientations, fall back to the configured
          // isIndoor so the size type still shows up under its usual tab.
          const matchesFallback = type.isIndoor === selectedIsIndoorFilter;

          if (!hasAvailability && !(isSoldOutEverywhere && matchesFallback)) {
            return null;
          }

          return {
            ...type,
            ...orientationStats,
            isIndoor: selectedIsIndoorFilter,
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          const availA = a.availableAmount > 0;
          const availB = b.availableAmount > 0;
          if (availA && !availB) return -1;
          if (!availA && availB) return 1;
          const volA = computeVolume(a);
          const volB = computeVolume(b);
          return volA - volB;
        });

      setSortedSizeTypes(projected);
    } else {
      setSortedSizeTypes(sizeTypes);
    }
  }, [sizeTypes, selectedIsIndoorFilter]);

  // Auto pick the first available size type to avoid empty details panel
  /*  useEffect(() => {
    if (
      !selectedSize &&
      Array.isArray(sortedSizeTypes) &&
      sortedSizeTypes.length > 0
    ) {
      for (const type of sortedSizeTypes) {
        if (type.availableAmount > 0) {
          setSelectedSize(type);
          break;
        }
      }
    }
  }, [sortedSizeTypes, selectedSize, setSelectedSize]); */

  // Set the indoor/outdoor filter and reset the selected size
  const toggleType = () => {
    setSelectedIsIndoorFilter(prev => !prev);
    setSelectedSize(null);
  };

  // Handle size selection - open sidebar on mobile
  const handleSizeSelect = type => {
    setSelectedSize(type);
    setSidebarOpen(true);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12">
      <div className="w-[85%] mx-auto col-span-1 md:col-span-7">
        <div className="mt-4 mb-4 containerList">
          <div className="flex justify-between items-center">
            <div className="font-bold text-lg">
              <div
                className="flex gap-2 items-center"
                style={{ border: '2px solid #233f72', padding: '4px' }}
              >
                <div
                  aria-pressed={!selectedIsIndoorFilter}
                  onClick={() => toggleType()}
                  className={`indoor-outdoor-toggle ${!selectedIsIndoorFilter ? 'bg-primaryblue p-1 text-white' : ''} cursor-pointer`}
                >
                  {t('outdoor')}
                </div>
                <div
                  onClick={() => toggleType()}
                  aria-pressed={selectedIsIndoorFilter}
                  className={`indoor-outdoor-toggle ${selectedIsIndoorFilter ? 'bg-primaryblue p-1 text-white' : ''} cursor-pointer`}
                >
                  {t('indoor')}
                </div>
              </div>
            </div>
            <div className="text-sm">{t('containers_are_dry_and_clean')}</div>
          </div>
        </div>
        <div className="mt-4 containerListitems flex flex-col gap-4">
          {Array.isArray(sortedSizeTypes) && sortedSizeTypes.length > 0 ? (
            sortedSizeTypes.map(type => (
              <SizeTypeItem
                key={type.id}
                type={type}
                selectedSize={selectedSize}
                setSelectedSize={handleSizeSelect}
                setSelectedLevel={setSelectedLevel}
              />
            ))
          ) : (
            <div className="text-gray-500 text-center py-8">
              {t('no_containers_available')}
            </div>
          )}
        </div>
        <div className="p-4 text-sm text-gray-600 flex flex-col gap-2">
          <p>{t('pick_size_container_list_small_text')}</p>
        </div>
      </div>

      {/* Desktop: show inline */}
      <div className="hidden md:block col-span-1 md:col-span-5">
        {selectedSize && (
          <ContainerInfoComponent
            roomList={roomList}
            selectedSize={selectedSize}
            selectedLevel={selectedLevel}
            setSelectedLevel={setSelectedLevel}
            handleNext={handleNext}
          />
        )}
      </div>

      {/* Mobile: show in sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onOpen={selectedSize ? () => setSidebarOpen(true) : null}
        openLabel={t('view_details')}
      >
        {selectedSize && (
          <ContainerInfoComponent
            roomList={roomList}
            selectedSize={selectedSize}
            selectedLevel={selectedLevel}
            setSelectedLevel={setSelectedLevel}
            handleNext={handleNext}
            onClose={() => setSidebarOpen(false)}
          />
        )}
      </Sidebar>
    </div>
  );
}
